/**
 * "Dial this number" / "attach to this call" seam between feature screens and the softphone dialer.
 *
 * The dialer lives in the sidebar dock (`c-sidebar-dock.vue`), which owns the popover, its anchor
 * element and the eligibility rules for showing a phone row at all. Rather than export that
 * component's internals, the dock registers handlers while it can actually place a call, and
 * anything else in the app just asks for a number to be dialed or an attachment to be run.
 *
 * With no dial handler registered — on mobile, or for a user with neither a softphone nor a starred
 * extension — a Talk user on a phone (native webview or phone browser) is handed to the standalone
 * ZynoTalk app's dialer with the number prefilled; anyone else falls through to a `tel:` link, which
 * hands off to whatever the device dials with. Attachments have no such fallback (a `tel:` link can't answer a
 * prepared offer): `requestAttachment` reports whether it was handed off so the caller can fall back
 * to `requestDial` with the equivalent dialed shortcut (`**<ext>`, a lot base, or a slot number).
 */

import { shallowRef, watch } from 'vue';

import type { AttachmentRequest } from '@/modules/talk/shared/softphone.manager';

import { dialViaZynoTalk, showZynoTalkLauncher } from '@/modules/talk/zynotalk-launcher';

type DialHandler = (phoneNumber: string) => void;

let dialHandler: DialHandler | null = null;

/** A directed pickup or exact parking retrieval, handed to the dialer to run from its selected extension. */
export type DialerAttachmentRequest = AttachmentRequest;

export interface DialerListenRequest {
    callId: string;
    label: string;
}

type AttachmentHandler = (request: DialerAttachmentRequest) => void;

let attachmentHandler: AttachmentHandler | null = null;

type ListenHandler = (request: DialerListenRequest) => void;

let listenHandler: ListenHandler | null = null;

/**
 * Reactive twin of `canDialInApp()`, for screens that decide what to render on it. The dock
 * registers once its own inputs (softphone access, starred extensions) have loaded, which can land
 * after a feature screen has mounted — a one-shot read at mount would miss it.
 */
export const dialerAvailable = shallowRef(false);

/** Registers the softphone dialer. Returns an unregister function for the caller's cleanup. */
export function registerDialHandler(handler: DialHandler): () => void {
    dialHandler = handler;
    dialerAvailable.value = true;
    return () => {
        // Guarded so a late cleanup from a previous registration can't unhook the current one.
        if (dialHandler === handler) {
            dialHandler = null;
            dialerAvailable.value = false;
        }
    };
}

/** True when the softphone dialer is available; `requestDial` still works either way. */
export function canDialInApp(): boolean {
    return !!dialHandler;
}

/**
 * True when a dial request would go through Talk — the in-app dialer, or (on a phone) the ZynoTalk
 * app — rather than the device's own `tel:` handler. Not reactive: read it at the moment of use.
 */
export function canDialViaTalk(): boolean {
    return canDialInApp() || showZynoTalkLauncher();
}

/**
 * Resolves once a Talk dial path exists, or after `timeoutMs` regardless. For a dial request that
 * arrives with the page (a `/talk?dial=` link): the dock registers its dialer only after its own
 * inputs load, so an immediate `requestDial` would fall through to `tel:` on a cold load.
 */
export function whenDialerReady(timeoutMs = 5000): Promise<void> {
    if (canDialViaTalk()) return Promise.resolve();
    return new Promise(resolve => {
        const done = () => {
            stop();
            clearTimeout(timer);
            resolve();
        };
        const stop = watch(dialerAvailable, available => available && done());
        const timer = setTimeout(done, timeoutMs);
    });
}

export function requestDial(phoneNumber: string) {
    if (dialHandler) {
        dialHandler(phoneNumber);
        return;
    }
    if (showZynoTalkLauncher()) {
        void dialViaZynoTalk(phoneNumber);
        return;
    }
    window.location.href = `tel:${phoneNumber}`;
}

/**
 * Reactive twin of `canAttachInApp()`. Unlike `dialerAvailable`, a screen offering exact
 * pickup/retrieval has no non-app fallback for the attachment itself — it should hide (or fall back
 * to a plain dialed shortcut) rather than offer a button that can only fail.
 */
export const attachmentAvailable = shallowRef(false);

/** Registers the softphone dialer's attachment runner. Returns an unregister function for cleanup. */
export function registerAttachmentHandler(handler: AttachmentHandler): () => void {
    attachmentHandler = handler;
    attachmentAvailable.value = true;
    return () => {
        // Guarded so a late cleanup from a previous registration can't unhook the current one.
        if (attachmentHandler === handler) {
            attachmentHandler = null;
            attachmentAvailable.value = false;
        }
    };
}

/** True when the softphone dialer can run an attachment; unlike `canDialInApp`, there is no fallback. */
export function canAttachInApp(): boolean {
    return !!attachmentHandler;
}

/**
 * Hands a directed pickup / exact parking retrieval to the dialer, which runs it from its selected
 * "Call From" extension. Returns `true` once handed off; `false` when nothing is registered, in
 * which case the caller should fall back to `requestDial` with the equivalent dialed shortcut.
 */
export function requestAttachment(request: DialerAttachmentRequest): boolean {
    if (attachmentHandler) {
        attachmentHandler(request);
        return true;
    }
    return false;
}

/** Reactive availability for exact live listening through the browser softphone. */
export const listenAvailable = shallowRef(false);

export function registerListenHandler(handler: ListenHandler): () => void {
    listenHandler = handler;
    listenAvailable.value = true;
    return () => {
        if (listenHandler === handler) {
            listenHandler = null;
            listenAvailable.value = false;
        }
    };
}

/** Hands one recording-authorized exact live call to the dialer's selected source extension. */
export function requestListen(request: DialerListenRequest): boolean {
    if (!listenHandler) return false;
    listenHandler(request);
    return true;
}
